/**
 * 代管中台（fleet）的顯示層共用邏輯：告警文案、水位色階、狀態中文、位元組格式。
 *
 * 門檻常數刻意與後端 `app/settings.py` 的告警 knobs 對齊（disk 80/90、cert 21/7、
 * host_offline 5/30 分）——UI 著色與伺服器實際發告警的界線一致，才不會出現
 * 「畫面綠的但告警頁紅一片」。後端 knob 若改，這裡要跟著改。
 */
import type { HostAlert } from '@/api/hosting'
import { activationPolicyLabel } from '@/lib/products'
import { ROLE_LABELS } from '@/lib/roles'
import type { StaleLevel } from '@/lib/staleness'

// --- 門檻（對齊 LS settings）---
export const DISK_WARN_PCT = 80
export const DISK_CRIT_PCT = 90
export const CERT_WARN_DAYS = 21
export const CERT_CRIT_DAYS = 7
/** agent 心跳 60s 一次 → 5 分沒回報＝warn、30 分＝crit（`host_offline_*_minutes`）。 */
export const AGENT_WARN_MINUTES = 5
export const AGENT_CRIT_MINUTES = 30

/** 水位（%）→ 三級色階；null＝沒資料（灰）。 */
export function meterLevel(pct: number | null | undefined): StaleLevel {
  if (pct == null) return 'ok'
  if (pct >= DISK_CRIT_PCT) return 'bad'
  if (pct >= DISK_WARN_PCT) return 'warn'
  return 'ok'
}

/**
 * agent 心跳新鮮度。`staleness.ts` 的 24h/72h 是 **CS 每小時心跳**校準的，
 * 套到 60 秒心跳的 agent 上會「掛了三小時還是綠的」——故本模組另立門檻，
 * 但沿用同一組 `StaleLevel` 與 `.seen/.warn/.bad` CSS。
 */
export function agentStaleLevel(iso: string | null | undefined, now: number = Date.now()): StaleLevel {
  if (!iso) return 'ok' // 尚未 enroll 的 pending 主機沒有心跳＝正常，不著色
  const minutes = (now - Date.parse(iso)) / 60_000
  if (minutes >= AGENT_CRIT_MINUTES) return 'bad'
  if (minutes >= AGENT_WARN_MINUTES) return 'warn'
  return 'ok'
}

/** 距今幾天（未來為正、過期為負）；null 進 null 出。 */
export function daysUntil(iso: string | null | undefined, now: number = Date.now()): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  return (t - now) / 86_400_000
}

/** 憑證到期著色：<7 天紅、<21 天橘（certbot 30 天自動續，21 天沒續＝壞了）。 */
export function certLevel(iso: string | null | undefined, now: number = Date.now()): StaleLevel {
  const days = daysUntil(iso, now)
  if (days == null) return 'ok'
  if (days < CERT_CRIT_DAYS) return 'bad'
  if (days < CERT_WARN_DAYS) return 'warn'
  return 'ok'
}

export function fmtBytes(n: number | null | undefined): string {
  if (n == null) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let v = n
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i += 1
  }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`
}

export function fmtPct(pct: number | null | undefined): string {
  return pct == null ? '—' : `${Math.round(pct)}%`
}

/**
 * 容量顯示。agent 的指標 payload 目前以 **MB** 計（`used_mb`／`total_mb`），
 * 協定草稿另有 bytes 版本 → 兩個都收，MB 優先。
 */
export function fmtCapacity(mb: number | null | undefined, bytes?: number | null): string {
  if (mb != null) return fmtBytes(mb * 1024 * 1024)
  if (bytes != null) return fmtBytes(bytes)
  return '—'
}

// --- 主機 / 站台狀態 ---
export const HOST_STATUS_LABELS: Record<string, string> = {
  pending: '待註冊',
  active: '運行中',
  draining: '排空中',
  retired: '已退役',
}
export function hostStatusLabel(s: string): string {
  return HOST_STATUS_LABELS[s] ?? s
}
/** 對應 ui.css 的 `.status-pill` 變體。 */
export function hostStatusClass(s: string): string {
  return { active: 'active', pending: 'pending', draining: 'pending', retired: 'superseded' }[s] ?? ''
}

export const SITE_STATE_LABELS: Record<string, string> = {
  provisioning: '開站中',
  running: '運行中',
  stopped: '已停站',
  decommissioning: '回收中',
  decommissioned: '已回收',
  failed: '失敗',
  missing: '盤點消失',
}
export function siteStateLabel(s: string): string {
  return SITE_STATE_LABELS[s] ?? s
}

/** TLS 狀態（agent readback／`issue_cert` 回寫的 `host_sites.tls_state`）。 */
export const TLS_STATE_LABELS: Record<string, string> = {
  issued: '已簽發',
  failed: '簽發失敗',
  pending_dns: '等待 DNS',
  none: '未簽發',
}
export function tlsStateLabel(s: string | null | undefined): string {
  return TLS_STATE_LABELS[s ?? 'none'] ?? (s || '未簽發')
}
/**
 * 站台現在有沒有可用的憑證。
 *
 * ★ `pending_dns` 刻意**不算異常**：那是 BYO 網域還沒指過來的正常等待。
 *   `none`／`failed` 才是「站台只剩 HTTP」——登入的 Secure cookie 存不下、
 *   通路 OAuth 的 https 回址檢查也會全失敗（見 `cert_missing` 告警）。
 */
export function tlsMissing(s: string | null | undefined): boolean {
  return (s ?? 'none') === 'none' || s === 'failed'
}

/**
 * 公開網址狀態（批135，`host_sites.pub_state`）。**只顯示、沒有操作鈕**（裁示 89：
 * 補網址只靠更新鏈，失敗下次更新自動重試）；代理商看得到同一份（裁示 117）。
 */
export const PUB_STATE_LABELS: Record<string, string> = {
  waiting: '等待中',
  enabling: '啟用中',
  active: '已啟用',
  failed: '失敗',
  unsupported: '版本不支援',
  none: '未設定',
}
export function pubStateLabel(s: string | null | undefined): string {
  return PUB_STATE_LABELS[s ?? 'none'] ?? (s || '未設定')
}
/** 狀態 → chip 樣式（沿用站台表既有的 `chip` 變體）。 */
export function pubStateChip(s: string | null | undefined): string {
  return { active: 'ok', failed: 'bad', unsupported: 'off' }[s ?? ''] ?? 'pend'
}
/**
 * 原因代碼 → 給人看的一句話。代碼來自中台（開站／DNS）與 agent（任務結果／盤點），
 * **未知代碼原樣顯示**——對照表不該吃掉新原因。
 */
export const PUB_DETAIL_LABELS: Record<string, string> = {
  // 中台自己寫的
  agent_outdated: '主機 agent 版本過舊，升級 agent 後於下次更新自動補上',
  tls_deferred: '等客服網域的 DNS 指過來、補簽憑證時一起啟用',
  no_public_ip: '主機尚未填對外 IP，DNS 需人工建立',
  dns_conflict: 'DNS 已有同名記錄指到別處，中台不覆寫——請人工確認',
  dns_manual: 'DNS 需人工建立',
  domain_mismatch: '主機上設定的公開網域與中台不符，下次更新會重新套用',
  not_configured: '主機上已沒有公開網址設定，下次更新會重新套用',
  cert_missing_domain: '憑證不含公開網域',
  guest_unhealthy: '公開網址的服務沒有回應',
  port_conflict: '公開 port 與同機其他站台衝突',
  unrecognized: '主機回報了無法辨識的原因',
  // agent 回報的（任務結果）
  bundle_unsupported: '目前安裝的產品版本不支援公開網址，升級後自動補上',
  site_not_running: '站台沒有在跑，下次更新再補',
  main_cert_pending: '客服網域的憑證還沒簽好，下次更新再補',
  dns_pending: '主機那一側還解析不到公開網域，下次更新再補',
  certbot_busy: '主機上的憑證工具正在忙，下次更新再補',
  nginx_busy: '主機上的網站設定正在被其他作業使用，下次更新再補',
  cert_pending: '憑證還沒擴充完成，下次更新再補',
  insufficient_time: '這次任務剩餘時間不夠，下次更新再補',
  retry_later: '暫時無法處理，下次更新再補',
  cert_residual: '憑證縮回失敗，客服網域的憑證仍含公開網域——需要人工處理（中台不會刪它的 DNS）',
  vhost_conflict: '主機上已有同名網站設定，已撤回',
  port_in_use: '公開 port 已被佔用，已撤回',
  health_timeout: '啟用後服務沒有在時限內就緒，已撤回',
  guest_unreachable: '啟用後連不上公開網址，已撤回',
  rollback_failed: '撤回失敗，需要人工處理',
  unexpected_error: '主機端發生未預期錯誤，已撤回',
}
export function pubDetailText(code: string | null | undefined): string | null {
  if (!code) return null
  return PUB_DETAIL_LABELS[code] ?? code
}
export function siteStateClass(s: string): string {
  return (
    {
      running: 'active',
      provisioning: 'pending',
      decommissioning: 'pending',
      stopped: 'superseded',
      decommissioned: 'superseded',
      failed: 'revoked',
      missing: 'revoked',
    }[s] ?? ''
  )
}

export function healthLabel(h: string | null): string {
  return { ok: '正常', fail: '異常', unknown: '未知' }[h ?? ''] ?? (h || '—')
}

// --- 告警 ---
/**
 * 批151 裁示 126③：站台機密被「只釋出（不派工）」之後，盤點又發現那一站在運作
 * （站台還拿著那把值，但授權中心已經可以把它給別站）。站台機密的確認文案也引用這個標籤。
 */
export const SITE_SECRET_RELEASED_ALERT = 'site_secret_released_site_alive'

/** 告警種類 → 中文（設計 `docs/PLAN-managed-hosting.md` §9；批B 起才有 job_failed 等）。 */
export const ALERT_KIND_LABELS: Record<string, string> = {
  host_offline: '主機離線',
  site_offline: '站台離線',
  disk_high: '磁碟水位過高',
  disk_forecast: '磁碟即將填滿',
  cert_expiring: '憑證即將到期',
  /** 憑證從來沒簽出來（站台只剩 HTTP）——比「即將到期」嚴重，登入與通路整合都會壞。 */
  cert_missing: '憑證未簽發',
  license_expiring: '授權即將到期',
  needs_setup: '尚未完成初始設定',
  backup_stale: '備份過期',
  /** 批80 批D：新證發出去了，站台卻還在跑舊證（授權派送鏈斷了）。 */
  site_license_drift: '站台仍在跑舊授權',
  /** 批80：授權綁的指紋 ≠ 主機當下的 machine anchors（VM 遷移／擴容／重建）→ CS 端會轉唯讀。 */
  site_fingerprint_drift: '站台授權指紋與主機不符',
  /** 批81 需求 5：代管試用即將／已經到期（到期當下中台會自動停站）。 */
  trial_expiring: '試用即將到期',
  /**
   * 批93-B：加購子證即將到期（事件式，`services/addons.reconcile_addon_expiry` 每小時掃一次）。
   * ★ 批169 順修：這一個 kind 一直沒有中文名，告警頁印的是原始英文 `addon_expiring`、說明欄整包 key=value。
   */
  addon_expiring: '加購子證即將到期',
  trial_expired_stopped: '試用到期已自動停站',
  /** 批81 需求 5：客戶在 portal 自助申請代管 CS，等我方核可開站。 */
  hosted_request_pending: '待核可的代管開站申請',
  /** 批93-C：有人確認送出了一筆授權申請，等我方審核核發。 */
  license_request_pending: '待審核的授權申請',
  /** 批93-C：superadmin 越過開通政策直接簽發（break-glass）——讓另一位當天看到。 */
  license_direct_issue: '未經申請的直接簽發',
  site_drift: '站台歸屬異動',
  site_oom: '容器記憶體不足',
  instance_conflict: 'instance 衝突',
  job_failed: '任務失敗',
  agent_outdated: 'agent 版本過舊',
  host_capacity: '主機容量不足',
  /** 批135 #42：補公開網址失敗、撤回也失敗（agent 的 `rollback_failed`）——要有人上主機處理。 */
  pub_needs_manual: '公開網址需人工處理',
  [SITE_SECRET_RELEASED_ALERT]: '站台機密已釋出但站台仍在主機上',
  /** 批155：平台送的子證超過模板宣告的上限（平台那邊收到 409，等營運在後台人工處理）。 */
  platform_addon_over_limit: '平台子證超過上限',
  /**
   * 批169（裁示 81）：部署的配額用量越過產品宣告的告警門檻（`QuotaSpec.usage_alert_pcts`）。
   * 部署層告警：自部署站也有，歸屬看 `customer_name`／`instance_name`；降回門檻以下自動解除。
   */
  quota_usage: '配額用量告警',
  /** 批192：依序更新遇到回滾也失敗／站台狀態不明的站，整筆執行暫停，等人看過再決定。 */
  upgrade_run_paused: '依序更新已暫停',
  /** 批192：LS 在維護時段開始後超過寬限才醒來，這一次的時段不補跑。 */
  upgrade_window_missed: '錯過維護時段',
}
export function alertKindLabel(kind: string): string {
  return ALERT_KIND_LABELS[kind] ?? kind
}

/**
 * 告警清單「對象」欄的文字（批169）。
 *
 * - 有站台 → 站台代號（既有行為）。
 * - 部署層告警（`scope='deploy'`）沒有站台（自部署站，或代理商看自家客戶開在我方主機上的站——那時
 *   主機與站台欄被後端隱去）→「客戶名・部署名」，部署名缺席時印 instance_id 前 12 碼。
 * - 其餘（主機層）→「主機」。
 */
export function alertTargetText(a: HostAlert): string {
  if (a.site_slug) return a.site_slug
  if (a.scope === 'deploy' || a.customer_id || a.instance_id) {
    const who = a.customer_name ?? null
    const inst = a.instance_name ?? (a.instance_id ? a.instance_id.slice(0, 12) : null)
    return [who, inst].filter(Boolean).join('・') || '部署'
  }
  return '主機'
}

export const SEVERITY_LABELS: Record<string, string> = { crit: '嚴重', warn: '警告', info: '提示' }
export function severityLabel(s: string): string {
  return SEVERITY_LABELS[s] ?? s
}

function num(v: unknown): number | null {
  return typeof v === 'number' ? v : null
}
function str(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null
}
function localTime(iso: unknown): string {
  const s = str(iso)
  if (!s) return '—'
  const t = Date.parse(s)
  return Number.isNaN(t) ? s : new Date(t).toLocaleString('zh-TW', { hour12: false })
}
const round1 = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1))
/** 批169：用量可能帶兩位小數（GiB 這類精確指標），整數照原樣印。 */
const round2 = (n: number) => (Number.isInteger(n) ? n.toLocaleString() : String(Math.round(n * 100) / 100))

/**
 * 告警 `detail`（每 kind 形狀不同的 JSONB）→ 一句人看得懂的話。
 * 未知 kind／缺鍵一律走 fallback 印原始鍵值，不吞資訊。
 */
export function alertDetailText(alert: HostAlert): string {
  const d = alert.detail ?? {}
  switch (alert.kind) {
    case 'host_offline': {
      const m = num(d.minutes)
      return m == null ? 'agent 從未回報心跳' : `agent 已 ${round1(m)} 分鐘沒回報`
    }
    case 'disk_high': {
      const p = num(d.used_pct)
      return p == null ? '磁碟水位過高' : `最高掛載點水位 ${round1(p)}%`
    }
    case 'disk_forecast': {
      const days = num(d.days_until_full)
      return days == null ? '磁碟成長中' : `依近日成長推估 ${round1(days)} 天後填滿`
    }
    case 'site_offline': {
      const reason =
        { missing: '盤點時找不到這站', health: '本機健康檢查連續失敗', heartbeat: 'CS 心跳逾時' }[
          str(d.reason) ?? ''
        ] ?? '無法連線'
      const state = str(d.state)
      return state ? `${reason}（狀態 ${siteStateLabel(state)}）` : reason
    }
    case 'cert_expiring': {
      const days = num(d.days)
      const at = d.expires_at ? `，到期 ${localTime(d.expires_at)}` : ''
      return `${days == null ? '憑證即將到期' : `憑證還有 ${round1(days)} 天`}${at}`
    }
    case 'cert_missing': {
      // failed＝certbot 跑過而且失敗；none＝從沒跑過。兩者都代表站台現在只有 HTTP。
      const why = str(d.tls_state) === 'failed' ? 'certbot 簽發失敗' : '從未簽發'
      const dom = str(d.domain)
      return `${dom ? `${dom} ` : ''}沒有憑證（${why}）——站台只有 HTTP，登入與通路整合都會失敗`
    }
    case 'license_expiring': {
      const days = num(d.days)
      const lic = str(d.license_id)
      return `${days == null ? '授權即將到期' : `授權還有 ${round1(days)} 天`}${lic ? `（${lic.slice(0, 8)}）` : ''}`
    }
    case 'needs_setup': {
      const days = num(d.days)
      return days == null ? '開站後尚未完成初始設定' : `開站已 ${round1(days)} 天仍未完成初始設定`
    }
    case 'backup_stale': {
      const at = d.latest_at ? `最後備份 ${localTime(d.latest_at)}` : '從未備份成功'
      const st = str(d.status)
      return st ? `${at}（狀態 ${st}）` : at
    }
    case 'site_license_drift': {
      const want = str(d.expected_license_id)
      const running = str(d.running_license_id)
      const mins = num(d.minutes)
      const age = mins == null ? '' : `已 ${round1(mins / 60)} 小時`
      return `站台仍在跑 ${running ? running.slice(0, 8) : '舊'} 授權，應為 ${
        want ? want.slice(0, 8) : '新證'
      }${age ? `（${age}）` : ''}——請查 agent 是否正常`
    }
    case 'site_fingerprint_drift': {
      // detail 只有鍵名沒有指紋值（那是綁機密鑰材料，刻意不外流到 UI／webhook）。
      const lic = str(d.license_id)
      return `站台授權指紋與主機不符${
        lic ? `（授權 ${lic.slice(0, 8)}）` : ''
      }——主機可能已遷移／擴容／重建，請到主機頁執行「整批重簽授權」`
    }
    case 'trial_expiring': {
      const days = num(d.days)
      const at = d.expires_at ? `，到期 ${localTime(d.expires_at)}` : ''
      if (days != null && days <= 0) {
        return `試用已到期${at}——停站任務應該已派出，若站台仍在運行請查 agent`
      }
      return `${days == null ? '試用即將到期' : `試用還有 ${round1(days)} 天`}${at}（到期會自動停站）`
    }
    case 'trial_expired_stopped': {
      const slug = str(d.slug)
      const job = str(d.job_id)
      return `${slug ? `站台 ${slug} ` : '站台'}因試用到期已派送停站${
        job ? `（任務 ${job}）` : ''
      }——資料與授權都保留，核可正式授權後會自動復站`
    }
    case 'hosted_request_pending': {
      const slug = str(d.slug)
      const who = str(d.requested_by)
      return `${who ?? '客戶'} 申請代管開站${slug ? `（代號 ${slug}）` : ''}——請到「授權申請」頁核可`
    }
    case 'license_request_pending': {
      // 批93-C：來源決定「這是誰在等我們」。detail 只放 id／客戶／來源／產品／kind／送件人
      // （**沒有內容**——那是要進後台看的東西，不該外流到 webhook）。
      const who = str(d.requested_by)
      const cust = str(d.customer_name)
      const origin =
        { reseller: '代理商', portal: '客戶自助', site: '站台', admin: '我方' }[
          str(d.origin) ?? ''
        ] ?? '有人'
      const kind =
        { new: '新授權', change: '變更授權', renew: '續約', addon: '加購' }[str(d.kind) ?? ''] ?? '授權'
      return `${origin}${who ? `（${who}）` : ''} 送出${cust ? ` ${cust} 的` : ''}${kind}申請——請到〈授權申請〉頁審核`
    }
    case 'license_direct_issue': {
      // break-glass：政策要求更高身分，但 superadmin 越權直發了。這不是錯誤，是要被看見。
      const actor = str(d.actor) ?? str(d.issued_by)
      const cust = str(d.customer_name)
      const lic = str(d.license_id)
      const policy = str(d.policy)
      return `${actor ?? `某位${ROLE_LABELS.superadmin}`} 未經申請直接簽發${cust ? ` ${cust} 的` : ''}授權${
        lic ? `（${lic.slice(0, 8)}）` : ''
      }${policy ? `，該內容的核發政策是「${activationPolicyLabel(policy)}」` : ''}——請確認這是預期中的例外`
    }
    case 'platform_addon_over_limit': {
      // 批155：平台替組織送子證，某個配額鍵超過模板的 `max`／`max_delta`。detail 只有識別與數值。
      const plat = str(d.platform_name) ?? str(d.platform_key) ?? str(d.platform)
      const org = str(d.external_ref) ?? str(d.customer_name)
      const tpl = str(d.addon_plan) ?? str(d.addon_plan_key)
      const key = str(d.label) ?? str(d.key)
      const value = num(d.value)
      const limit = num(d.limit)
      return `${plat ? `平台「${plat}」` : '平台'}替${org ? `組織 ${org}` : '旗下組織'}送的子證${
        tpl ? `（模板 ${tpl}）` : ''
      }${key ? `，「${key}」` : ''}${value != null ? `數值 ${value}` : ''}${
        limit != null ? ` 超過上限 ${limit}` : '超過上限'
      }——平台已收到「需人工處理」，請到客戶頁評估後在後台加購`
    }
    case 'addon_expiring': {
      // 批169 順修：detail＝{addon_id, label, instance_id, customer_id, days, expires_at}（後端只放識別與期限）。
      const label = str(d.label)
      const days = num(d.days)
      const at = d.expires_at ? `，到期 ${localTime(d.expires_at)}` : ''
      const iid = str(d.instance_id)
      const who = `加購子證${label ? `「${label}」` : ''}`
      const when =
        days == null ? '即將到期' : days <= 0 ? `已過期 ${round1(-days)} 天` : `還有 ${round1(days)} 天到期`
      return `${who}${when}${at}${iid ? `（部署 ${iid.slice(0, 12)}）` : ''}——請確認客戶要不要續購`
    }
    case 'quota_usage': {
      // 批169：detail＝{key, label, used, limit, pct, threshold}（label 是告警產生當下的憑證設計標籤，
      // 前端不必再查產品；用量可能帶小數——精確指標）。
      const label = str(d.label) ?? str(d.key) ?? '配額'
      const used = num(d.used)
      const limit = num(d.limit)
      const pct = num(d.pct)
      const th = num(d.threshold)
      const nums = used != null && limit != null ? ` ${round2(used)} / ${round2(limit)}` : ''
      const pctText = pct != null ? `（${round1(pct)}%）` : ''
      const tail = th != null && th >= 100 ? '——已達上限' : th != null ? `——已超過 ${th}% 門檻` : ''
      return `「${label}」已用${nums}${pctText}${tail}；降回門檻以下自動解除`
    }
    case 'site_drift': {
      const iid = str(d.instance_id)
      return `instance ${iid ? iid.slice(0, 16) : '？'} 被別台主機盤點到（未自動搬移，請人工確認）`
    }
    case 'site_oom': {
      const restarts = num(d.restarts)
      const killed = d.killed === true
      const parts = [killed ? '容器曾被 OOM 終止' : '容器重啟次數異常']
      if (restarts != null) parts.push(`restart ${restarts} 次`)
      return parts.join('，')
    }
    case 'job_failed': {
      const kind = str(d.kind)
      const err = str(d.error)
      return `${kind ? `任務 ${kind} ` : '任務'}失敗${err ? `：${err}` : ''}`
    }
    case 'agent_outdated': {
      const cur = str(d.version)
      const latest = str(d.latest)
      return `agent ${cur ?? '？'}${latest ? ` → 最新 ${latest}` : ''}`
    }
    case 'pub_needs_manual': {
      // detail 只有代碼與識別（沒有 agent 的 message——那可能含 certbot 原文），細節看任務紀錄。
      const dom = str(d.pub_domain)
      const job = str(d.job_id)
      const code = str(d.code)
      // `rollback_failed` 帶 `cause`＝原本的失敗；`needs_manual` 的其他代碼（例 unexpected_error）本身就是原因。
      // 刻意印代碼不套 `pubDetailText`：那份對照的句子多半以「已撤回」結尾，放在這裡會自相矛盾。
      const why = str(d.cause) ?? (code && code !== 'rollback_failed' ? code : null)
      const whyText = why ? `（原因代碼 ${why}）` : ''
      return `${dom ? `${dom} ` : ''}補公開網址失敗${whyText}，撤回也沒有完成——請上主機處理${
        job ? `（任務 ${job}）` : ''
      }；之後的任務或盤點確認已啟用、或已清除且服務正常時自動解除`
    }
    default: {
      const entries = Object.entries(d).filter(([, v]) => v !== null && typeof v !== 'object')
      return entries.length ? entries.map(([k, v]) => `${k}=${String(v)}`).join('、') : '—'
    }
  }
}
